import { savedWeek } from '../../plan/saved';

/** Legacy addresses remain aliases to current patient places; no patient facts are inferred here. */
export type PatientPlace = 'home' | 'start' | 'plans' | 'chat' | 'scan' | 'scans' | 'me' | 'card' | 'programs' | 'week' | 'own' | 'assessment' | 'programme' | 'visit' | 'call';
export type PatientSheet = 'medals' | 'invite' | 'subscription' | 'settings';
export interface PatientEntry { place: PatientPlace; sheet?: PatientSheet; program?: string; scan?: string; claimScan?: string; href: string }
const flags: Partial<Record<PatientPlace,string>> = {chat:'chat',scan:'scan',scans:'scans',me:'profile',card:'patient-card',own:'exercises',week:'week',assessment:'assessment',visit:'visit',call:'call'};
export function patientEntry(path: string, search: string, hash = ''): PatientEntry {
  const route = path.replace(/\/+$/, '') || '/', q = new URLSearchParams(search);
  q.delete('intro');
  if(['quick','scan'].includes(q.get('start')??''))q.delete('start');
  if(q.has('program')&&!/^[a-z0-9-]+$/.test(q.get('program')!))q.delete('program');
  let place: PatientPlace = 'home', sheet: PatientSheet | undefined, program: string | undefined;
  if(q.get('sheet')==='chat'&&q.get('id')){
    const nested=new URLSearchParams(q.get('id')!);
    for(const key of ['new','mode','conversation','draft','return','start','signin','visit'])if(nested.has(key)&&!q.has(key))q.set(key,nested.get(key)!);
    if(q.has('new')&&!q.has('mode'))q.set('mode','history_aware');
  }
  if(q.get('sheet')==='program'&&q.get('id')&&/^[a-z0-9-]+$/.test(q.get('id')!))q.set('program',q.get('id')!);
  if (route !== '/home' && q.has('new') && !q.has('mode')) q.set('mode','history_aware');
  const isProgram = /^\/program\/([a-z0-9-]+)$/.exec(route);
  const known = ['/', '/home', '/chat', '/account', '/settings', '/scans', '/exercises', '/week', '/agent', '/history'].includes(route) || !!isProgram;
  if (!known) { const lang = q.get('lang'); return {place:'home',href:`/home${lang === 'en' || lang === 'sq' ? `?lang=${lang}` : ''}`}; }
  if (route === '/settings') { place = 'me'; sheet = 'settings'; }
  else if (route === '/account') {
    place = 'me'; sheet = hash === '#plan' ? 'subscription' : hash === '#achievements' ? 'medals' : undefined;
    if (q.has('scan')) { q.set('claimScan', q.get('scan')!); q.delete('scan'); }
  }
  else if (route === '/scans') place = 'scans';
  else if (route === '/exercises') place = 'own';
  else if (route === '/week') place = 'week';
  else if (route === '/agent') place = q.get('do') === 'scan' ? 'scan' : 'assessment';
  else if (isProgram) { program=isProgram[1];place=program==='full-body'?'programs':'programme'; }
  else if (route === '/chat') place = 'chat';
  else if (route !== '/history') {
    const open = q.get('open');
    if (open === 'start' || open === 'plans') place = open;
    else if (open === 'settings' || open === 'medals' || open === 'invite' || open === 'subscription') { place = 'me'; sheet = open; }
    else if (open === 'profile' || q.get('profile') === '1') place = 'me';
    else if (q.get('visit') === '1' && !q.has('signin')) place = 'visit';
    else if (q.get('call') === '1') place = 'call';
    else if (q.get('patient-card') === '1') place = 'card';
    else if (q.get('scans') === '1') place = 'scans';
    else if (q.has('scan') || q.get('sheet') === 'scan') place = 'scan';
    else if (q.get('week') === '1') place = 'week';
    else if (q.get('exercises') === '1') place = 'own';
    else if (q.get('assessment') === '1' || q.get('sheet') === 'pain') place = 'assessment';
    else if (q.has('program') && q.get('program') !== 'full-body') { place = 'programme'; program = q.get('program')!; }
    else if (open === 'programs' || q.get('program') === 'full-body' || hash === '#programs') {place = 'programs';program=q.get('program')??undefined;}
    // The retired Home card was an umbrella: a saved week took priority over own exercises.
    // Explicit /exercises and ?exercises=1 links above still keep their original destination.
    else if (q.get('sheet') === 'exercises') place = savedWeek() ? 'week' : 'own';
    else if (q.get('chat') === '1' || q.get('sheet') === 'chat' || q.has('conversation') || q.has('draft') || q.has('signin') || q.has('visit') || q.has('return') || q.get('start') === 'consult' || (q.has('new') && q.has('mode'))) place = 'chat';
  }
  const scan = place === 'scan' && q.get('scan') && q.get('scan') !== '1' ? q.get('scan')! : undefined;
  for (const key of ['legacy','sheet','card','her','do','id']) q.delete(key);
  if (route === '/history') { for (const key of [...q.keys()]) if (key !== 'lang') q.delete(key); }
  if (flags[place]) q.set(flags[place]!, place === 'scan' && scan ? scan : '1');
  if (program) q.set('program', program);
  if (sheet) q.set('open', sheet);
  if ((place === 'programs' && !q.has('program')) || place === 'start' || place === 'plans') q.set('open',place);
  return {place,sheet,program,scan,claimScan:q.get('claimScan') ?? undefined,href:`/home${q.size ? `?${q}` : ''}${['#plan','#achievements','#programs'].includes(hash)?'':hash}`};
}
/** A closed direct bookmark must reload its destination, never the exited plan/assessment.
 * Only locale is carried: this is navigation, not copying conversation or clinical data. */
export function patientCloseAddress(place: string, search: string): string {
  const q = new URLSearchParams(),lang = new URLSearchParams(search).get('lang');
  if (lang === 'en' || lang === 'sq') q.set('lang',lang);
  const flag = Object.entries(flags).find(([key])=>key === place)?.[1];
  if (flag) q.set(flag,'1');
  else if (place === 'start' || place === 'plans' || place === 'programs') q.set('open',place);
  return '/home' + (q.size ? `?${q}` : '');
}
/** Sign-out must also discard a deep-linked conversation before the next person opens Chat. */
export function clearConversationAddress() {
  const url = new URL(window.location.href);
  for (const key of ['conversation','new','mode','draft','signin','visit','return','start']) url.searchParams.delete(key);
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}
